import type { LevelId } from '../../data/ids';
import { t, type StringKey } from '../../i18n/es';
import type { SaveStore } from '../../save/save-store';
import { missionRows, nextMissionIndex, worldGroups, type MissionStatus } from './mission-rows';

export interface MissionScreenOptions {
  store: () => SaveStore;
  onSelect: (levelId: LevelId) => void;
  onOpenSettings: () => void;
  onOpenCredits: () => void;
}

const STATUS_KEYS: Record<MissionStatus, StringKey> = {
  BLOQUEADA: 'ui.misiones.status.locked',
  DISPONIBLE: 'ui.misiones.status.available',
  COMPLETADA: 'ui.misiones.status.completed',
};

/**
 * MISIONES (GDD §13.2, §10.1): the 12 operations with their threat and status for the active slot, and
 * the side bar MISIONES / AJUSTES / CRÉDITOS. Rows move with the arrow keys (roving tabindex); locked rows
 * are aria-disabled and cannot be selected. Native elements until the design system lands (#15).
 */
export function misionesScreen(options: MissionScreenOptions): HTMLElement {
  const save = options.store().save;
  const slot = save.lastSlotId ? save.slots[save.lastSlotId] : null;
  const rows = missionRows(slot);
  const start = nextMissionIndex(rows);

  const section = document.createElement('section');
  section.className = 'missions';
  section.setAttribute('aria-labelledby', 'missions-title');
  section.innerHTML = `
    <nav class="side-bar" aria-label="${t('ui.screen.misiones')}">
      <button type="button" class="button" aria-current="page">${t('ui.misiones.nav.missions')}</button>
      <button type="button" class="button" data-nav="settings">${t('ui.misiones.nav.settings')}</button>
      <button type="button" class="button" data-nav="credits">${t('ui.misiones.nav.credits')}</button>
    </nav>
    <div class="missions-table">
      <h1 id="missions-title" class="tactical">${t('ui.screen.misiones')}</h1>
      <table>
        <thead><tr>
          <th scope="col">${t('ui.misiones.column.operation')}</th>
          <th scope="col">${t('ui.misiones.column.id')}</th>
          <th scope="col">${t('ui.misiones.column.threat')}</th>
          <th scope="col">${t('ui.misiones.column.status')}</th>
        </tr></thead>
        <tbody>${worldGroups(rows)
          .map(
            (group) => `
          <tr class="world-row" data-world="${group.world}">
            <th scope="colgroup" colspan="4"><span class="tactical">${group.name}</span>${
              group.completed ? ` <span class="badge" data-field="world-completed">${t('ui.misiones.world.completed')}</span>` : ''
            }</th>
          </tr>${rows
            .map((row, index) => ({ row, index }))
            .filter(({ row }) => row.world === group.world)
            .map(
              ({ row, index }) => `
          <tr data-op="${row.op}" data-level="${row.levelId}" data-status="${row.status}"
              tabindex="${index === start ? 0 : -1}" aria-disabled="${row.status === 'BLOQUEADA'}">
            <td class="display-mono" data-field="name">${row.name}</td>
            <td class="readout" data-field="op">${row.op}</td>
            <td data-field="threat">${row.threat}</td>
            <td><span class="badge" data-field="status">${t(STATUS_KEYS[row.status])}</span>${
              row.best ? ` <span class="readout" data-field="best">${row.best}</span>` : ''
            }${row.assisted ? ` <span class="badge" data-field="assisted">${t('ui.misiones.assisted')}</span>` : ''}${row.comingSoon ? ` <span class="muted" data-field="note">${t('ui.misiones.comingSoon')}</span>` : ''}</td>
          </tr>`,
            )
            .join('')}`,
          )
          .join('')}</tbody>
      </table>
    </div>`;

  const rowElements = [...section.querySelectorAll<HTMLTableRowElement>('tbody tr[data-op]')];
  const focusRow = (index: number) => {
    const target = rowElements[Math.max(0, Math.min(index, rowElements.length - 1))]!;
    for (const row of rowElements) row.tabIndex = row === target ? 0 : -1;
    target.focus();
  };
  const select = (row: HTMLTableRowElement) => {
    if (row.getAttribute('aria-disabled') === 'true') return;
    options.onSelect(row.dataset.level as LevelId);
  };

  section.querySelector('tbody')!.addEventListener('keydown', (event) => {
    const index = rowElements.indexOf(event.target as HTMLTableRowElement);
    if (index < 0) return;
    if (event.key === 'ArrowDown') focusRow(index + 1);
    else if (event.key === 'ArrowUp') focusRow(index - 1);
    else if (event.key === 'Home') focusRow(0);
    else if (event.key === 'End') focusRow(rowElements.length - 1);
    else if (event.key === 'Enter' || event.key === ' ') select(rowElements[index]!);
    else return;
    event.preventDefault();
  });
  section.querySelector('tbody')!.addEventListener('click', (event) => {
    const row = (event.target as HTMLElement).closest<HTMLTableRowElement>('tr[data-op]');
    if (row) select(row);
  });
  section.querySelector('[data-nav="settings"]')!.addEventListener('click', options.onOpenSettings);
  section.querySelector('[data-nav="credits"]')!.addEventListener('click', options.onOpenCredits);

  queueMicrotask(() => focusRow(start));
  return section;
}
